import { useEffect, useState } from 'react';
import type { OpenStatus } from '../../lib/twin';
import { CtaButton } from './CtaButton';

interface Props {
  status: OpenStatus;
  onOpenBot: () => void;
}

/**
 * Mobile-only sticky "Order Now" bar. Appears after the hero scrolls out of view
 * so the primary CTA is always one tap away. Hidden on `md+` (the desktop hero
 * keeps its CTAs visible) and from print.
 */
export function StickyOrderBar({ status, onOpenBot }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const sentinel = document.getElementById('top');
    if (!sentinel || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-120px 0px 0px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 border-t border-mocha/15 bg-cream-50/95 px-4 py-3 shadow-[0_-4px_16px_rgba(44,26,16,0.12)] backdrop-blur transition-transform duration-200 md:hidden print:hidden ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-3">
        <p className="flex-1 text-sm font-semibold text-espresso">
          <span className={status.isOpen ? 'text-open' : 'text-closed'}>
            {status.isOpen ? 'Open now' : 'Closed'}
          </span>{' '}
          · Bean Counter Cafe
        </p>
        <CtaButton
          onClick={onOpenBot}
          variant="secondary"
          ariaLabel="Ask our barista bot — opens a chat panel"
        >
          Ask bot
        </CtaButton>
        <CtaButton href="#menu" variant="primary">
          Order Now
        </CtaButton>
      </div>
    </div>
  );
}
